import { Injectable } from '@angular/core';
import { NavigationEnd, NavigationExtras, Router } from '@angular/router';

import { BehaviorSubject, Observable, catchError, filter, map, of, shareReplay, switchMap, tap } from 'rxjs';

import { MfaModel, MfaStatusModel, MfaVerifyModel } from '../../Core/Users/models/mfa.model';

import { UsersApi } from '../../api/users/users.api';
import { MfaApiModel } from '../../api/users/models/mfa.api.model';

import { LoginInfo, LoginModel } from '../models/login-info.model';
import { LoginResult, LoginService, LoginType } from './login.service';

@Injectable()
export class MfaService {
    private mfaStatusSubject: BehaviorSubject<MfaStatusModel> = new BehaviorSubject<MfaStatusModel>(null);
    private currentUrlSubject = new BehaviorSubject<string>(undefined);

    // available and confirmed setup options (used for MFA setup and login)
    mfaStatus$ = this.mfaStatusSubject.asObservable().pipe(
        shareReplay(1)
    );

    all$ = this.currentUrlSubject.asObservable().pipe(
        switchMap((currentUrl) => {
            return this.setGetAllRequest(currentUrl);
        }),
        map(allMfaSettings => {
            const mfaSettings = allMfaSettings?.methods ? allMfaSettings.methods : [];
            return mfaSettings.map(x => new MfaModel(x));
        })

    );
    useAdminApi: boolean;
    userId: number;
    getAllRequest: Observable<any> = of(null);
    currentUrl: string;

    private loginInfo: LoginInfo = new LoginInfo();
    constructor(
        private usersApi: UsersApi,
        private router: Router,
        private loginService: LoginService
    ) {
        // HACK: somehow router events aren't triggered when navigating by address bar, which causes the list to be empty
        // use window location to get the current url instead
        if (window.location.href) {
            this.currentUrl = window.location.href;
            this.refreshAllMfaSetting();
        }
        this.router.events.pipe(
            filter(x => !!x),
            tap(e => {
                if (e instanceof NavigationEnd) {
                    this.currentUrl = e.urlAfterRedirects;
                    this.refreshAllMfaSetting();
                }
            })
        ).subscribe();

    }

    refreshAllMfaSetting() {
        this.currentUrlSubject.next(this.currentUrl);
    }

    setGetAllRequest(currentUrl: string): Observable<any> {
        const urlParse: RegExp = /AccountManagement\/(user)\/(\d+)|UserPreferences/;
        const matches = currentUrl?.match(urlParse);
        if (matches) {
            if (matches[1] === 'user') {
                this.useAdminApi = true;
                this.userId = matches[2] ? +matches[2] : null;
                return this.usersApi.getAllMfaSettings(this.userId);
            } else if (matches[0] === 'UserPreferences') {
                this.useAdminApi = false;
                return this.usersApi.getCurrentUserMfaSettings();
            }

        } else {
            return of(null);
        }
    }

    setCurrentMfaStatus(status: MfaStatusModel) {
        this.mfaStatusSubject.next(status);
    }

    /**
     * Set preferred mfa method for a given user
     * @param userId - for UMT
     */
    setPreferredMethod(mfaMethod: MfaModel): Observable<MfaApiModel> {
        mfaMethod.preferred = true;
        if (this.useAdminApi && this.userId) {
            return this.usersApi.updateMfaSettingForUser(this.userId, mfaMethod.toMfaVerifyModel());
        } else {
            return this.usersApi.updateMfaSetting(mfaMethod.toMfaVerifyModel());
        }
    }

    /**
     * Set preferred mfa method for a given user
     * @param userId - for UMT
     */
    deleteMethod(mfaMethod: MfaModel): Observable<void> {
        if (this.useAdminApi && this.userId) {
            return this.usersApi.deleteMfaSettingForUser(this.userId, mfaMethod.id);
        } else {
            return this.usersApi.deleteMfaSetting(mfaMethod.id);
        }

    }

    createNewMfaMethod(): Observable<MfaStatusModel> {
        return this.usersApi.createMfaSetting().pipe(
            tap(x => this.setCurrentMfaStatus(x))
        );
    }

    setLoginInfo(loginInfo: LoginInfo) {
        this.loginInfo = loginInfo;
    }

    getLoginInfo(): LoginInfo {
        return this.loginInfo;
    }

    performMfaLogin(mfaCredentials: MfaVerifyModel): Observable<any> {
        const loginModel: LoginModel = {
            loginInfo: {
                username: this.loginInfo.username,
                password: this.loginInfo.password
            },
            forceLogin: false,
            returnUrl: ''
        };
        return this.loginService.performMfaLogin(loginModel, mfaCredentials).pipe(
            tap((result: LoginResult) => {
                if (result.loginType === LoginType.MFA) {
                    // first leg of sign in workflow
                    this.navigateToMfaCodePage(result, loginModel);
                }
            })
        );
    }

    navigateToMfaCodePage(result: LoginResult, loginModel: LoginModel) {
        this.setLoginInfo(loginModel.loginInfo);
        this.setCurrentMfaStatus(result.mfaResponse);
        const navExtras: NavigationExtras = loginModel.returnUrl ? { queryParams: { returnUrl: loginModel.returnUrl }}: {};
        this.router.navigate(['MultiFactorAuthentication'], navExtras);
    }
}
